import { FormattedMessage } from "react-intl";
import { Grid, useMediaQuery } from "@mui/material";
import { Add, Create, Delete } from "@mui/icons-material";

import { SelectInput } from "@dzambalaorg/mui-inputs-core";
import { SearchWithFilters } from "@dzambalaorg/mui-form-search";
import { Breadcrumbs, PageHeader, ProgressOverlay } from "@dzambalaorg/mui-page-layout";
import { DeleteDialog } from "@dzambalaorg/mui-dialog-delete";
import { getEmptyTemplate } from "@dzambalaorg/mui-inputs-asset";
import { useDialogDelete, useFetchQuery } from "@dzambalaorg/react-hooks";
import { IPaginationResult } from "@dzambalaorg/types-collection";
import { formatItem } from "@framework/exchange";
import { ListAction, ListActions, StyledListItem, StyledListWrapper, StyledPagination } from "@framework/styled";
import { CraftStatus, ICraft, TokenType } from "@framework/types";
import { useGlobalDialogFormCallbacks } from "@framework/shared";
import { Button, FormGrid, List } from "@dzambalaorg/ui-kit";

import { craftFilter, prepareCraftInitialValues } from "./schemas";
import { craftFormValidationSchema } from "./form/validation";
import { AdminDialogKeys } from "../../../../../shared";

const CRAFT_URL = "/recipes/craft";
const emptyValues = {
  item: getEmptyTemplate(TokenType.ERC721),
  price: getEmptyTemplate(TokenType.ERC1155),
};

export const Craft = () => {
  const isMd = useMediaQuery(theme => theme.breakpoints.down("md"));

  const { isLoading, take, skip, onChangePage, hasNextPage, loadMoreRef, allRows, totalCount, refetch } = useFetchQuery<
    IPaginationResult<ICraft>
  >(CRAFT_URL, { enableInfiniteScroll: isMd }, void 0, {
    craftStatus: [CraftStatus.ACTIVE],
  });

  const { openDialog } = useGlobalDialogFormCallbacks<ICraft, ICraft>();
  const { handleDelete } = useDialogDelete<ICraft>();

  const craftCreate = () => {
    openDialog(AdminDialogKeys.CRAFT_CREATE, emptyValues as ICraft, {
      refetch,
      initialValues: emptyValues as ICraft,
      message: "dialogs.create",
      testId: "CraftFormDialog",
      filter: craftFilter,
      url: CRAFT_URL,
      validationSchema: craftFormValidationSchema,
      componentProps: {
        mode: "create",
      },
    });
  };

  const craftEdit = (craft: ICraft) => () => {
    openDialog(AdminDialogKeys.CRAFT_EDIT, craft, {
      refetch,
      initialValues: craft,
      message: "dialogs.edit",
      testId: "CraftFormDialog",
      url: `${CRAFT_URL}/${craft.id}`,
      validationSchema: craftFormValidationSchema,
      filter: craftFilter,
      prepareInitialValues: prepareCraftInitialValues,
      componentProps: {
        mode: "edit",
      },
    });
  };

  return (
    <Grid>
      <Breadcrumbs path={["dashboard", "recipes", "recipes.craft"]} />

      <PageHeader message="pages.recipes.craft.title">
        <Button intent="secondary" startIcon={<Add />} onClick={craftCreate} data-testid="CraftCreateButton">
          <FormattedMessage id="form.buttons.create" />
        </Button>
      </PageHeader>

      <SearchWithFilters initialValues={{ craftStatus: [CraftStatus.ACTIVE] }}>
        <FormGrid
          columns={[1]}
          containerProps={{ sx: theme => ({ marginTop: theme.spacing(theme.tokens.spacing.xl) }) }}
        >
          <SelectInput multiple name="craftStatus" options={CraftStatus} />
        </FormGrid>
      </SearchWithFilters>

      <ProgressOverlay isLoading={isLoading}>
        <StyledListWrapper count={allRows.length} isLoading={isLoading}>
          {allRows.map(craft => (
            <StyledListItem key={craft.id}>
              <List.ItemText sx={{ width: 0.3, px: 0.5 }}>{formatItem(craft.price)}</List.ItemText>
              <List.ItemText sx={{ width: 0.3, px: 0.5 }}>{formatItem(craft.item)}</List.ItemText>
              <ListActions>
                <ListAction
                  onClick={craftEdit(craft)}
                  message="form.buttons.edit"
                  dataTestId="CraftEditButton"
                  icon={Create}
                />
                <ListAction
                  onClick={handleDelete(craft)}
                  message="form.buttons.delete"
                  dataTestId="CraftDeleteButton"
                  icon={Delete}
                />
              </ListActions>
            </StyledListItem>
          ))}
        </StyledListWrapper>
      </ProgressOverlay>

      {!isMd && totalCount > take && (
        <StyledPagination
          shape="rounded"
          page={skip / take + 1}
          count={Math.ceil(totalCount / take)}
          onChange={onChangePage}
        />
      )}
      {isMd && hasNextPage && <div ref={loadMoreRef} style={{ height: "20px", margin: "20px 0" }} />}

      <DeleteDialog url={CRAFT_URL} refetch={refetch} />
    </Grid>
  );
};
